import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, finalize, throwError, EMPTY } from 'rxjs';
import { MessageService } from 'primeng/api';
import { LoginService } from '@/services/login.service';
import { LoaderService, SKIP_LOADER } from '@/UtilService/loader.service';
import { ClientService } from '@/services/client.service';

let authErrorShown = false;

export const apiInterceptor: HttpInterceptorFn = (req, next) => {
    const router = inject(Router);
    const messageService = inject(MessageService);
    const loginService = inject(LoginService);
    const loaderService = inject(LoaderService);
    const clientService = inject(ClientService);

    const token = loginService.getAccessToken();

    // Skip loader for some endpoints (optional), or when the caller opted out
    // for this specific request via SKIP_LOADER.
    const skipLoader = req.context.get(SKIP_LOADER) || req.url.includes('/assets/') || req.url.includes('/token') || req.url.includes('/notification-announcement') || req.url.includes('/document-status') || req.url.includes('/announcement/count') || req.url.includes('/user-manual/list');

    if (!skipLoader) {
        loaderService.show();
    }

    // Build headers
    let headers = req.headers.set('Accept', 'application/json');
    if (token) {
        headers = headers.set('Authorization', `Bearer ${token}`);
    }

    const authReq = req.clone({ headers });

    return next(authReq).pipe(
        catchError((error: any) => {
            // Ignore cancellations from fetch driver (Angular withFetch)
            if (isAbortError(error)) {
                // Do not show toasts or propagate; treat as a silent cancellation
                return EMPTY;
            }

            handleError(error as HttpErrorResponse, router,req, messageService, clientService);
            return throwError(() => error);
        }),
        finalize(() => {
            if (!skipLoader) {
                loaderService.hide();
            }
        })
    );
};

function isAbortError(error: any): boolean {
    // DOMException from fetch abort
    if (error instanceof DOMException && error.name === 'AbortError') {
        return true;
    }
    // Some environments wrap as plain object
    const name = (error && typeof error === 'object') ? (error.name || '') : '';
    if (name === 'AbortError') {
        return true;
    }
    // Fallback: HttpErrorResponse with status 0 and message mentioning Abort
    if (error && typeof error === 'object' && 'status' in error && error.status === 0) {
        const msg = String(error.message || '');
        const statusText = String(error.statusText || '');
        if (msg.includes('Abort') || statusText.includes('Abort')) {
            return true;
        }
    }
    return false;
}

function handleError(error: HttpErrorResponse, router: Router, req: any, messageService: MessageService, clientService: ClientService): void {
    if (error.status === 401 && !req.url.includes('/token')) {
        if (!authErrorShown) {
            authErrorShown = true;
            messageService.add({
                severity: 'info',
                summary: 'Session Expired',
                detail: 'Your session has expired. Please log in again.',
                life: 3000
            });

            setTimeout(() => {
                authErrorShown = false;
            }, 3500);

            localStorage.clear(); // Clear all stored data
            clientService.clearClientId();
            router.navigate(['/login']); // Redirect to login
        }
    } else if (error.status === 403) {
        if (!authErrorShown) {
            authErrorShown = true;

            router.navigate(['/forbidden']); // Redirect to 403 page
        }
    } else if (error.status === 404) {
        router.navigate(['/not-found']); // Redirect to 404 page
    }
}
